import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { Cashflow } from '@/types/statistics'

import { useEffect, useMemo, useRef, useState } from 'react'

import { useGetPortfolioStatsMutation } from '@/legacy-api/analytics-slice'

interface UseCashflowDataParams {
  holdings: Array<{
    isin: string
    quantity: number
  }>
}

interface CashflowData {
  monthly_cashflows: Cashflow[]
  yearly_cashflows: Cashflow[]
  all_cashflows: Cashflow[]
}

interface UseCashflowDataReturn {
  cashflowData: CashflowData
  isLoading: boolean
  error: SerializedError | FetchBaseQueryError | null
}

export function useCashflowData({
  holdings,
}: UseCashflowDataParams): UseCashflowDataReturn {
  const [getPortfolioStats] = useGetPortfolioStatsMutation()
  const [cashflowData, setCashflowData] = useState<{
    monthly_cashflows: Cashflow[]
    yearly_cashflows: Cashflow[]
    all_cashflows: Cashflow[]
  }>({
    monthly_cashflows: [],
    yearly_cashflows: [],
    all_cashflows: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<
    SerializedError | FetchBaseQueryError | null
  >(null)
  const requestIdRef = useRef(0)

  const holdingsKey = useMemo(
    () =>
      JSON.stringify(
        holdings.map((holding) => ({
          instrument_id: holding.isin,
          size: { value: holding.quantity },
        })),
      ),
    [holdings],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  useEffect(() => {
    const currentRequestId = ++requestIdRef.current

    // Avoid refetching when holdings payload is empty
    if (!holdingsKey || holdingsKey === '[]') {
      setCashflowData({
        monthly_cashflows: [],
        yearly_cashflows: [],
        all_cashflows: [],
      })
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    getPortfolioStats({
      holdings: JSON.parse(holdingsKey),
    })
      .unwrap()
      .then((response) => {
        if (requestIdRef.current === currentRequestId) {
          setCashflowData({
            monthly_cashflows: response.monthly_cashflows || [],
            yearly_cashflows: response.yearly_cashflows || [],
            all_cashflows: response.all_cashflows || [],
          })
        }
      })
      .catch((err) => {
        if (requestIdRef.current === currentRequestId) {
          setError(err)
          setCashflowData({
            monthly_cashflows: [],
            yearly_cashflows: [],
            all_cashflows: [],
          })
        }
      })
      .finally(() => {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      })
  }, [holdingsKey])

  return {
    cashflowData,
    isLoading,
    error,
  }
}
